import { useState, useEffect, useRef } from "react";
import "./reportsdownloadqueue.css";
import {
    Table,
    Input,
    Button,
    Space,
    Spin,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import { SearchOutlined } from "@ant-design/icons";
import CommonObj from "../../CommonObj";
import { getUTCDate } from "../../components/utcDate";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { useAuth } from "../../auth/AuthContext";
import { exportToExcel } from "../exporttoexcel/recycleexporttoexcel";
import { runGridExport } from "../../components/gridExportHelper";
import RefreshIcon from "../../assets/img/svg/xrefresh-circle.svg";
import DownloadIcon from "../../assets/img/svg/xdownload.svg";
import TBIIcon from "../../assets/img/svg/xtbi-status.svg";
import CompletedIcon from "../../assets/img/svg/xcompleted-status.svg";
import FailedIcon from "../../assets/img/svg/xfailed-status.svg";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";

const Reportsdownloadqueue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortedInfo, setSortedInfo] = useState({});
    const [searchFilters, setsearchFilters] = useState({});
    const { roleUserId } = useAuth();
    const { accessToken } = useAuthToken();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const searchInput = useRef(null);

    const handleReset = (dataIndex, confirm, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({ pagination, sorter: sortedInfo, filters: { ...searchFilters, [dataIndex]: undefined } });
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        Search
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            handleReset(dataIndex, confirm, clearFilters);
                            confirm();
                        }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
        ),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        sorter: true,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
    });

    const handleDownload = async (key) => {
        if (!key) {
            message.info(`Not a valid key`);
            return;
        }

        const fileNameWithExtension = key;
        const URL = CommonObj.Endpoints.GetFileFromS3 + `?key=${fileNameWithExtension}`;
        const response = await fetch(URL, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            },
        });

        if (!response.ok) {
            message.error("Failed to download");
            return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;

        const contentDisposition = response.headers.get("content-disposition");
        let downloadFileName = key.split("/").pop();

        if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
                downloadFileName = decodeURIComponent(match[1]);
            }
        }
        let fileDateStr = "";
        const timeRegex = /_(\d{8}_\d{6})\./;
        const timeMatch = downloadFileName.match(timeRegex);

        if (timeMatch) {
            fileDateStr = timeMatch[1];
            try {
                const dateTimeStr = timeMatch[1];

                const year = parseInt(dateTimeStr.substring(0, 4), 10);
                const month = parseInt(dateTimeStr.substring(4, 6), 10) - 1;
                const day = parseInt(dateTimeStr.substring(6, 8), 10);
                const hour = parseInt(dateTimeStr.substring(9, 11), 10);
                const min = parseInt(dateTimeStr.substring(11, 13), 10);
                const sec = parseInt(dateTimeStr.substring(13, 15), 10);

                const utcDate = new Date(Date.UTC(year, month, day, hour, min, sec));

                const formatter = new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/Chicago',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hourCycle: 'h23'
                });

                const parts = {};
                formatter.formatToParts(utcDate).forEach(({ type, value }) => {
                    parts[type] = value;
                });

                const cstDateStr = `${parts.year}${parts.month}${parts.day}_${parts.hour}${parts.minute}${parts.second}`;

                fileDateStr = cstDateStr;
                downloadFileName = downloadFileName.replace(dateTimeStr, cstDateStr);
            } catch (error) {
                console.warn("Could not convert UTC to CST in filename.", error);
            }
        }

        if (downloadFileName.includes("ODBC")) {
            if (fileDateStr) {
                link.setAttribute("download", `BID_ODBC_${fileDateStr}.csv`);
            } else {
                link.setAttribute("download", "BID_ODBC.csv");
            }
        } else {
            link.setAttribute("download", downloadFileName);
        }
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    };

    const handleRefresh = () => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
    };

    const columns = [
        {
            title: "Report Name",
            dataIndex: "reportName",
            key: "reportName",
            width: 220,
            sorter: (a, b) => a.reportName.localeCompare(b.reportName),
            ...getColumnSearchProps("reportName"),
        },
        {
            title: "Requested Date & Time",
            dataIndex: "requestedDate",
            key: "requestedDate",
            width: 200,
            sorter: (a, b) => a.requestedDate.localeCompare(b.requestedDate),
        },
        {
            title: "Duration",
            dataIndex: "durationRange",
            key: "durationRange",
            width: 200,
        },
        {
            title: "Filters",
            dataIndex: "requestedFilters",
            key: "requestedFilters",
            width: 150,
        },
        {
            title: "Status",
            dataIndex: "reportStatus",
            key: "reportStatus",
            width: 100,
            sorter: (a, b) => a.reportStatus.localeCompare(b.reportStatus),
            ...getColumnSearchProps("reportStatus"),
            render: (_, record) => (
                <Space size="middle">
                    <div
                        className={`grid-badge ${
                            record.reportStatus
                                ? record.reportStatus.toLowerCase() === "processing"
                                    ? "tbi"
                                    : record.reportStatus.toLowerCase() === "processed"
                                    ? "completed"
                                    : "failed"
                                : "tbi"
                        }`}
                        title={
                            record.reportStatus.toLowerCase() === "failed"
                                ? "Report generation failed due to system error. Please retry."
                                : ""
                        }
                    >
                        <span>
                            {record.reportStatus === "Processing" && <img src={TBIIcon} alt="Processing" />}
                            {record.reportStatus === "Processed" && <img src={CompletedIcon} alt="Completed" />}
                            {record.reportStatus === "Failed" && <img src={FailedIcon} alt="Failed" />}
                        </span>
                        <span>{record.reportStatus}</span>
                    </div>
                </Space>
            ),
        },
        {
            title: "Action",
            key: "actions",
            width: 100,
            align: "center",
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="text"
                        title={
                            record.isDownloadRequired.toLowerCase() === "no" ? "" : "Download"
                        }
                        icon={
                            <img
                                src={DownloadIcon}
                                alt="Download"
                                className={
                                    " " +
                                    (record.isDownloadRequired.toLowerCase() === "no"
                                        ? "icon-disabled"
                                        : "")
                                }
                            />
                        }
                        disabled={record.isDownloadRequired.toLowerCase() === "no"}
                        onClick={() => handleDownload(record.fileName)}
                    />
                </Space>
            ),
        },
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };

    const handleExport = async () => {
        const payload = {
            pageNo: 1,
            pageSize: 0,
            search: JSON.stringify(searchFilters),
            orderByColumn: sortedInfo?.field || "RequestedDate",
            orderBy: sortedInfo?.order === "ascend" ? "ASC" : "DESC",
            userId: roleUserId,
        };
        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp(),
        };
        await runGridExport({
            request: fetch(CommonObj.Endpoints.GetReportProcessingDetails, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            }),
            exportFn: exportToExcel,
            columns: columns.filter((col) => col.key !== "actions"),
            filename: buildTimestampedFileName("Reports Download Queue"),
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "RequestedDate",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
            userId: roleUserId,
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetReportProcessingDetails, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item) => ({
                ...item,
                key: item.referenceId,
            }));
            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
    }, []);

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Reports Download Queue</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="active-page-header-button-container">
                        <Button
                            type="text"
                            title={"Refresh Data"}
                            icon={<img src={RefreshIcon} alt="Refresh Data" />}
                            onClick={() => handleRefresh()}
                        />
                        <button
                            className="active-page-header-button"
                            onClick={handleExport}
                        >
                            <span>
                                <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <title>Download Data</title>
                                    <g clipPath="url(#clip0_875_15157)">
                                        <path
                                            d="M11.5447 3.04024L11.1433 3.48621L11.1433 3.48621L11.5447 3.04024ZM14.5138 5.71241L14.1124 6.15839L14.5138 5.71241ZM16.2405 7.61557L15.6924 7.85967L15.6924 7.85968L16.2405 7.61557ZM2.37868 15.6213L2.80294 15.1971H2.80294L2.37868 15.6213ZM15.6213 15.6213L15.1971 15.1971L15.6213 15.6213ZM10.5 16.5V15.9H7.5V16.5V17.1H10.5V16.5ZM1.5 10.5H2.1V7.5H1.5H0.9V10.5H1.5ZM16.5 10.1722H15.9V10.5H16.5H17.1V10.1722H16.5ZM11.5447 3.04024L11.1433 3.48621L14.1124 6.15839L14.5138 5.71241L14.9152 5.26643L11.9461 2.59426L11.5447 3.04024ZM16.5 10.1722H17.1C17.1 8.90922 17.1121 8.09787 16.7886 7.37147L16.2405 7.61557L15.6924 7.85968C15.8879 8.29862 15.9 8.80322 15.9 10.1722H16.5ZM14.5138 5.71241L14.1124 6.15839C15.1299 7.07417 15.4969 7.42073 15.6924 7.85967L16.2405 7.61557L16.7886 7.37147C16.4651 6.64507 15.8539 6.11131 14.9152 5.26643L14.5138 5.71241ZM7.52234 1.5V2.1C8.71153 2.1 9.15094 2.10926 9.54196 2.25931L9.75692 1.69914L9.97188 1.13897C9.325 0.890737 8.62066 0.9 7.52234 0.9V1.5ZM11.5447 3.04024L11.9461 2.59426C11.1337 1.8631 10.6187 1.38716 9.97188 1.13897L9.75692 1.69914L9.54196 2.25931C9.93306 2.40939 10.2639 2.69476 11.1433 3.48621L11.5447 3.04024ZM7.5 16.5V15.9C6.06882 15.9 5.0558 15.8987 4.28813 15.7955C3.53772 15.6946 3.11222 15.5063 2.80294 15.1971L2.37868 15.6213L1.95442 16.0456C2.52382 16.615 3.24476 16.866 4.12824 16.9848C4.99445 17.1013 6.10275 17.1 7.5 17.1V16.5ZM1.5 10.5H0.9C0.9 11.8973 0.898726 13.0055 1.01519 13.8718C1.13397 14.7552 1.38501 15.4762 1.95442 16.0456L2.37868 15.6213L2.80294 15.1971C2.49367 14.8878 2.30537 14.4623 2.20448 13.7119C2.10127 12.9442 2.1 11.9312 2.1 10.5H1.5ZM10.5 16.5V17.1C11.8973 17.1 13.0055 17.1013 13.8718 16.9848C14.7552 16.866 15.4762 16.615 16.0456 16.0456L15.6213 15.6213L15.1971 15.1971C14.8878 15.5063 14.4623 15.6946 13.7119 15.7955C12.9442 15.8987 11.9312 15.9 10.5 15.9V16.5ZM16.5 10.5H15.9C15.9 11.9312 15.8987 12.9442 15.7955 13.7119C15.6946 14.4623 15.5063 14.8878 15.1971 15.1971L15.6213 15.6213L16.0456 16.0456C16.615 15.4762 16.866 14.7552 16.9848 13.8718C17.1013 13.0055 17.1 11.8973 17.1 10.5H16.5ZM1.5 7.5H2.1C2.1 6.06882 2.10127 5.0558 2.20448 4.28813C2.30537 3.53772 2.49367 3.11222 2.80294 2.80294L2.37868 2.37868L1.95442 1.95442C1.38501 2.52382 1.13397 3.24476 1.01519 4.12824C0.898726 4.99445 0.9 6.10275 0.9 7.5H1.5ZM7.52234 1.5V0.9C6.11759 0.9 5.0039 0.898736 4.13418 1.01514C3.24765 1.13379 2.52428 1.38455 1.95442 1.95442L2.37868 2.37868L2.80294 2.80294C3.11175 2.49413 3.53856 2.30555 4.29336 2.20453C5.06497 2.10126 6.08378 2.1 7.52234 2.1V1.5Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M9.75 1.875V3.75C9.75 5.51777 9.75 6.40165 10.2992 6.95083C10.8483 7.5 11.7322 7.5 13.5 7.5H16.5"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M6.375 10.125L6.375 13.875M6.375 13.875L7.875 12.4688M6.375 13.875L4.875 12.4688"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_875_15157">
                                            <rect width="18" height="18" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                              pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters,
                                    sortedInfo
                                }),
                        }}
                        rowKey={(record) => record.bidNo}
                        scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                        rowClassName={getRowClassName}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default Reportsdownloadqueue;